"use client";

import FAQ from "@/components/FAQ";
import { FlexRowAlignCenter } from "@/components/Flex";
import Footer from "@/components/Footer";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import { S3UploadJobContextProvider } from "@knowt/syncing/context/S3UploadJobContext";
import { AICompletionType, FlashcardSet, Note } from "@knowt/syncing/graphql/schema";
import { TOOL_TITLE } from "@knowt/syncing/utils/aiTools";
import { ChevronRight } from "lucide-react";
import { ReviewType } from "../LandingPages/LandingMainPage/constants";
import ExploreSection from "../LandingPages/UploadLandingPages/components/ExploreSection";
import UsersFeedbacks from "../LandingPages/UploadLandingPages/components/UsersFeedbacks";
import {
    LandingFullWidthSection,
    LandingHalfSection,
    LandingPageWrapper,
} from "../LandingPages/components/styled/Containers";
import OtherTools from "./components/OtherTools";
import ToolSettings from "./components/ToolSettings";
import { TEACHER_AI_TOOLS_FAQS, TEACHER_AI_TOOL_FAQS, ToolBenefits } from "./utils/constants";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { ConditionalLink } from "@/components/ConditionalLink";
import BenefitsCards from "./components/BenefitsCards";
import BrowseEncouragementCards from "../LandingPages/components/BrowseEncouragmentCards";
import { ASSETS_URL } from "@/config/deployConstants";

const Crumb = ({ href, title }: { href?: string; title: string }) => {
    const isLink = !!href;

    return (
        <ConditionalLink href={href} condition={isLink}>
            <span className="bodyBold2">{title}</span>
        </ConditionalLink>
    );
};

const AIToolBreadcrumb = ({ currentTool }: { currentTool: string }) => {
    return (
        <FlexRowAlignCenter
            style={{ padding: "2.5rem 4rem 0", backgroundColor: themeColors.background, gap: spacing.XS }}>
            <Crumb href={"/free-ai-tools-for-teachers"} title={"Teacher Tools"} />
            <ChevronRight size={24} color={themeColors.neutralBlack} />
            <Crumb title={TOOL_TITLE[currentTool]} />
        </FlexRowAlignCenter>
    );
};

const TeacherAIToolPage = ({
    tool,
    topNotes,
    topFlashcardSets,
}: { tool: AICompletionType; topNotes: Note[]; topFlashcardSets: FlashcardSet[] }) => {
    const { user } = useCurrentUser();
    return (
        <LandingPageWrapper>
            <AIToolBreadcrumb currentTool={tool} />
            <S3UploadJobContextProvider>
                <LandingHalfSection style={{ paddingTop: spacing.MD }}>
                    <ToolSettings tool={tool} />
                </LandingHalfSection>

                {!user && (
                    <>
                        <LandingFullWidthSection style={{ padding: "8rem 0" }}>
                            <UsersFeedbacks
                                type={ReviewType.SCHOOLS}
                                title="Trusted by 1,000+ schools"
                                isForSchools
                                subtitle={"Join our thriving community of teachers & students that trust Knowt."}
                            />
                        </LandingFullWidthSection>

                        <LandingHalfSection style={{ padding: "8rem 0" }}>
                            <h2 className="heading2" style={{ marginBottom: spacing.XS_2 }}>
                                Create instant lesson plans in any format
                            </h2>
                            <p className="bodyXl" style={{ marginBottom: spacing.LG_3 }}>
                                Tell us the exact topic you’re planing to teach, what format you want and if you have
                                anything specific in mind. We’ll do the rest.
                            </p>
                            <FlexRowAlignCenter
                                style={{
                                    gap: spacing.MD,
                                    alignItems: "stretch",
                                    flexWrap: "wrap",
                                    marginBottom: spacing.LG_3,
                                }}>
                                <BenefitsCards benefits={ToolBenefits[tool]} />
                            </FlexRowAlignCenter>
                            <FlexRowAlignCenter style={{ gap: spacing.MD, alignItems: "stretch", width: "100%" }}>
                                <BrowseEncouragementCards
                                    encouragementCards={[
                                        {
                                            title: "Become a Knowt Knight & earn!",
                                            href: "/teachers/knowt-knights-teacher-ambassadors",
                                            image: `${ASSETS_URL}/images/knowt-ambassador-benefits.svg`,
                                        },
                                        {
                                            title: "No steep learning curve",
                                            href: "https://help.knowt.com",
                                            image: `${ASSETS_URL}/images/learning-curve.svg`,
                                        },
                                    ]}
                                />
                            </FlexRowAlignCenter>
                        </LandingHalfSection>

                        <LandingHalfSection style={{ padding: "8rem 0" }}>
                            {/* TODO: Extract ExploreSection inner components */}
                            <ExploreSection topNotes={topNotes} topFlashcardSets={topFlashcardSets} examsLength={0} />
                        </LandingHalfSection>

                        <LandingHalfSection style={{ padding: "8rem 0" }}>
                            <OtherTools tool={tool} />
                        </LandingHalfSection>

                        <LandingHalfSection style={{ padding: "8rem 0", maxWidth: "70rem" }}>
                            <FAQ
                                faqs={TEACHER_AI_TOOL_FAQS[tool] ?? TEACHER_AI_TOOLS_FAQS}
                                title={"FAQ"}
                                description={"We thought you might have some questions..."}
                                titleClassName="heading2"
                                containerStyle={{
                                    marginTop: 0,
                                    backgroundColor: themeColors.background,
                                }}
                            />
                        </LandingHalfSection>
                    </>
                )}
            </S3UploadJobContextProvider>
            <Footer landing />
        </LandingPageWrapper>
    );
};

export default TeacherAIToolPage;
